import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import predictionService from '../services/predictionService';

const PredictionsDashboard = () => {
  const navigate = useNavigate();
  
  // State management
  const [predictions, setPredictions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    riskLevel: '',
    agent: '',
    campaign: '',
    limit: 50,
    offset: 0
  });
  
  // Dialog state
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Load predictions data
  const loadPredictions = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      
      const response = await predictionService.getPredictionsDashboard(filters);
      
      if (response.success) {
        console.log('=== PREDICTIONS DATA DEBUG ===');
        console.log('Full response:', response.data);
        console.log('Predictions array:', response.data.predictions);
        // Log first prediction structure for debugging
        if (response.data.predictions && response.data.predictions.length > 0) {
          console.log('First prediction structure:', response.data.predictions[0]);
        }
        
        setPredictions(response.data.predictions || []);
        setStats(response.data.stats || {});
      } else {
        setError(response.error || 'Failed to load predictions');
        setPredictions([]);
        setStats({});
      }
    } catch (err) {
      console.error('Error loading predictions:', err);
      setError('Failed to load predictions data');
      setPredictions([]);
      setStats({});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadPredictions();
  }, []);

  // Refresh data
  const handleRefresh = () => {
    setRefreshing(true);
    loadPredictions(false);
  };

  // Apply filters
  const applyFilters = () => {
    setFiltersOpen(false);
    loadPredictions();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
      riskLevel: '',
      agent: '',
      campaign: '',
      limit: 50,
      offset: 0
    });
  };

  // Handle prediction details
  const showPredictionDetails = (prediction) => {
    setSelectedPrediction(prediction);
    setDetailsOpen(true);
  };

  // Navigate to call analysis
  const viewCallAnalysis = (callId) => {
    navigate(`/call-analysis?callId=${callId}`);
  };

  // Get risk level display
  const getRiskDisplay = (riskLevel, score) => {
    const color = predictionService.getRiskColor(riskLevel);
    const icon = predictionService.getRiskIcon(riskLevel);
    
    const IconComponent = {
      warning: WarningIcon,
      info: InfoIcon,
      check_circle: CheckCircleIcon
    }[icon] || InfoIcon;

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <IconComponent style={{ color, fontSize: 20 }} />
        <Typography variant="body2" style={{ color }}>
          {riskLevel.toUpperCase()} ({predictionService.formatScore(score)})
        </Typography>
      </Box>
    );
  };

  // Stats cards data
  const statsCards = [
    {
      title: 'Total Predictions',
      value: stats.totalPredictions || 0,
      icon: AnalyticsIcon,
      color: '#2196f3'
    },
    {
      title: 'High Risk Calls',
      value: stats.highRisk || 0,
      icon: WarningIcon,
      color: '#f44336'
    },
    {
      title: 'Medium Risk Calls',
      value: stats.mediumRisk || 0,
      icon: InfoIcon,
      color: '#ff9800'
    },
    {
      title: 'Low Risk Calls',
      value: stats.lowRisk || 0,
      icon: CheckCircleIcon,
      color: '#4caf50'
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          🎯 Call Outcome Predictions
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFiltersOpen(true)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      {card.title}
                    </Typography>
                    <Typography variant="h4" component="h2">
                      {card.value}
                    </Typography>
                  </Box>
                  <card.icon style={{ fontSize: 40, color: card.color }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Predictions Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Predictions
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Risk Level</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                                  {predictions.map((prediction) => (
                    <TableRow key={prediction.id || Math.random()} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {prediction.call?.phone || prediction.call?.phoneNumber || prediction.call?.phone_number || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {prediction.call?.agent?.name || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {prediction.call?.campaign || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {getRiskDisplay(prediction.riskLevel, prediction.predictionScore)}
                    </TableCell>
                    <TableCell>
                                              <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={(prediction.confidence || 0) * 100}
                            sx={{ width: 60, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="body2">
                            {predictionService.formatConfidence(prediction.confidence || 0)}
                          </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {(() => {
                          const createdDate = prediction.createdAt || prediction.created_at || prediction.call?.createdAt || prediction.call?.created_at;
                          if (createdDate) {
                            try {
                              return format(new Date(createdDate), 'MMM dd, HH:mm');
                            } catch (error) {
                              console.error('Error formatting date:', createdDate, error);
                              return 'Invalid Date';
                            }
                          }
                          return 'N/A';
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => showPredictionDetails(prediction)}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Call Analysis">
                          <IconButton 
                            size="small" 
                            onClick={() => viewCallAnalysis(prediction.call?.id || prediction.callId)}
                          >
                            <PlayIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {predictions.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">
                No predictions found for the selected criteria
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Filters Dialog */}
      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Filter Predictions</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  startDate: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  endDate: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Risk Level</InputLabel>
                <Select
                  value={filters.riskLevel}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    riskLevel: e.target.value
                  }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="high">High Risk</MenuItem>
                  <MenuItem value="medium">Medium Risk</MenuItem>
                  <MenuItem value="low">Low Risk</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Campaign"
                value={filters.campaign}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  campaign: e.target.value
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetFilters}>Reset</Button>
          <Button onClick={() => setFiltersOpen(false)}>Cancel</Button>
          <Button onClick={applyFilters} variant="contained">Apply Filters</Button>
        </DialogActions>
      </Dialog>

      {/* Prediction Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Prediction Details</DialogTitle>
        <DialogContent>
          {selectedPrediction && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Call ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedPrediction.call?.uniqueId || selectedPrediction.callId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Risk Assessment
                  </Typography>
                  {getRiskDisplay(selectedPrediction.riskLevel, selectedPrediction.predictionScore)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Confidence Level
                  </Typography>
                  <Typography variant="body1">
                    {predictionService.formatConfidence(selectedPrediction.confidence)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Analysis Timeframe
                  </Typography>
                  <Typography variant="body1">
                    {selectedPrediction.timeframe} seconds
                  </Typography>
                </Grid>
              </Grid>

              {/* Key Indicators */}
              {selectedPrediction.keyIndicators && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Key Indicators
                  </Typography>
                  {selectedPrediction.keyIndicators.positiveSignals?.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="success.main">
                        Positive Signals
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                        {selectedPrediction.keyIndicators.positiveSignals.map((signal, index) => (
                          <Chip key={index} label={signal} color="success" size="small" />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {selectedPrediction.keyIndicators.negativeSignals?.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="error.main">
                        Negative Signals
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                        {selectedPrediction.keyIndicators.negativeSignals.map((signal, index) => (
                          <Chip key={index} label={signal} color="error" size="small" />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Recommendations */}
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Recommendations
                </Typography>
                <Box component="ul" pl={2}>
                  {predictionService.getRecommendations(selectedPrediction).map((rec, index) => (
                    <Typography component="li" key={index} variant="body2" mb={1}>
                      {rec}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {/* Sentiment Analysis */}
              {selectedPrediction.sentimentAnalysis && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Sentiment Analysis
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Customer Sentiment
                      </Typography>
                      <Typography variant="body1">
                        {selectedPrediction.sentimentAnalysis.customerSentiment}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Agent Tone
                      </Typography>
                      <Typography variant="body1">
                        {selectedPrediction.sentimentAnalysis.agentTone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Overall Mood
                      </Typography>
                      <Typography variant="body1">
                        {selectedPrediction.sentimentAnalysis.overallMood}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                      <Button 
              onClick={() => viewCallAnalysis(selectedPrediction?.call?.id || selectedPrediction?.callId)}
              variant="contained"
            >
              View Call Analysis
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PredictionsDashboard; 